import { useEffect, useRef, useState } from 'react';
import { useFiltersStore } from '@/features/filters/store';
import { useChoroplethStore } from '@/features/choropleth/store';
import { usePoiStore } from '@/features/poi/store';
import { usePlacesStore } from '@/features/places/store';
import { useMapStore } from '@/features/map-core/store';
import { useScoringStore } from '@/lib/scoring/store';
import { encodeUrlState, decodeUrlState, type UrlState } from '@/lib/url-state';

function applyUrlState(url: UrlState) {
  if (url.layer) useChoroplethStore.setState({ activeLayer: url.layer });
  if (url.mode) useChoroplethStore.setState({ mode: url.mode });

  // Вьюпорт из URL приоритетнее сохранённого и дефолтов из meta.json —
  // ссылка должна открываться ровно там, куда её поставили.
  if (url.center && url.zoom !== undefined) {
    useMapStore.setState({
      center: url.center,
      zoom: url.zoom,
      viewportFromUrl: true,
      needsMetaDefaults: false,
    });
  } else if (url.center) {
    useMapStore.setState({
      center: url.center,
      viewportFromUrl: true,
      needsMetaDefaults: false,
    });
  } else if (url.zoom !== undefined) {
    useMapStore.setState({
      zoom: url.zoom,
      viewportFromUrl: true,
      needsMetaDefaults: false,
    });
  }

  if (url.filters) {
    useFiltersStore.setState({
      raw: url.filters,
      applied: url.filters,
      presetActive: url.preset ?? null,
    });
  } else if (url.preset) {
    // Пресеты страны приезжают вместе с meta.json, а этот код выполняется
    // синхронно на монтировании — раньше загрузки. Поэтому id запоминаем
    // всегда, а фильтры применяются либо здесь (если список уже известен),
    // либо в FiltersPanel, как только он появится.
    useFiltersStore.setState({ presetActive: url.preset });
    useFiltersStore.getState().applyPreset(url.preset, { silent: true });
  }

  if (url.poi && url.poi.length > 0) {
    usePoiStore.setState({ active: new Set(url.poi) });
  }

  if (url.selection) {
    usePlacesStore.setState({ selection: url.selection });
  }

  if (url.profile) {
    useScoringStore.setState({ activePresetId: url.profile, customWeights: null });
  }
}

function readCurrentUrlState(): UrlState {
  const filters = useFiltersStore.getState();
  const choro = useChoroplethStore.getState();
  const poi = usePoiStore.getState();
  const places = usePlacesStore.getState();
  const map = useMapStore.getState();
  const scoring = useScoringStore.getState();
  // Preserve fields that originate from the URL (not from stores) so they
  // survive refresh + every store-driven hash rewrite. Without this, a user
  // who lands on `/it/map#fq=1&q=<answers>` loses the quiz context on the
  // first filter toggle, breaking the QuizProfileBanner across reloads.
  const existing = decodeUrlState(typeof window !== 'undefined' ? window.location.hash : '');
  return {
    layer: choro.activeLayer,
    mode: choro.mode,
    zoom: map.zoom,
    center: map.center,
    filters: filters.raw,
    preset: filters.presetActive ?? undefined,
    poi: Array.from(poi.active),
    selection: places.selection,
    profile: scoring.activePresetId,
    quizResultBlob: existing.quizResultBlob,
    fromQuiz: existing.fromQuiz,
  };
}

export function useUrlSync() {
  // Lazy init runs synchronously before first render — applies URL state on every mount
  // so client-side navigation from /quiz/result → /it/map#<preset> picks up the new hash.
  useState(() => {
    const url = decodeUrlState(window.location.hash);
    applyUrlState(url);
    return null;
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const writeUrl = () => {
      const encoded = encodeUrlState(readCurrentUrlState());
      const newHash = '#' + encoded;
      if (window.location.hash !== newHash) {
        history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search + newHash,
        );
      }
    };

    const schedule = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(writeUrl, 300);
    };

    // Initial write: канонизируем URL по реальному состоянию (incl. persisted selection)
    writeUrl();

    const unsubs = [
      useFiltersStore.subscribe(schedule),
      useChoroplethStore.subscribe(schedule),
      usePoiStore.subscribe(schedule),
      usePlacesStore.subscribe(schedule),
      useMapStore.subscribe(schedule),
      useScoringStore.subscribe(schedule),
    ];

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      unsubs.forEach((u) => u());
    };
  }, []);
}
